import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';
import WorkingHoursEditor, { WorkingHour } from '../../components/WorkingHoursEditor';

export default function SetSchedule() {
  const supabase = getSupabaseClient();
  const navigate = useNavigate();

  const [workingHours, setWorkingHours] = useState<WorkingHour[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = workingHours.some(h => h.is_active);

  const handleWorkingHoursChange = (hours: WorkingHour[]) => {
    setWorkingHours(hours);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setError('Authentication error. Please log in again.');
        setSaving(false);
        return;
      }

      // Save working hours via RPC
      const { error: hoursError } = await supabase
        .rpc('upsert_guide_working_hours', {
          p_guide_id: user.id,
          p_hours: workingHours,
        });

      if (hoursError) {
        console.error('Error saving working hours:', hoursError);
        throw new Error(hoursError.message || 'Failed to save working hours');
      }

      // Navigate back to complete profile
      navigate('/onboarding');
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcef] px-6">
      <div className="mx-auto w-[90vw] max-w-4xl py-12 text-[#304031]">
        {/* Header */}
        <h1 className="mb-4 text-[42px] leading-[44px] font-medium text-[#304031] font-recoleta">
          Set available times for your consultation call
        </h1>

        {/* Green box */}
        <div className="mb-8 rounded-2xl bg-[#214D3C] p-6 text-[#FFFCEF]">
          <img
            src="/icon.svg"
            alt=""
            className="w-6 h-6 mb-4 [filter:brightness(0)_invert(98%)_sepia(6%)_saturate(365%)_hue-rotate(338deg)_brightness(103%)]"
          />
          <h3 className="text-lg font-bold mb-3">Set available times for your consultation call</h3>
          <p className="text-sm leading-[1.4]">
            Let seekers know when you're available for a consultation call. These hours will be displayed to the seeker when they schedule the first consultation call with you so you both can get acquainted. You can update these anytime from your schedule page. No pressure - choose hours that work with your schedule.
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Working Hours Editor */}
        <div className="mb-8">
          <WorkingHoursEditor
            onChange={handleWorkingHoursChange}
            showSaveButton={false}
          />
        </div>

        {/* Why we ask section */}
        <div className="mb-8 rounded-2xl border border-[#E8DFBD] bg-white p-6">
          <div className="flex items-center gap-2 mb-4">
            <img src="/lightbulb.svg" alt="" className="w-5 h-5" />
            <h3 className="text-base font-semibold text-[#304031]">Why we ask this</h3>
          </div>
          <ul className="space-y-2 text-sm text-[#304031]">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#304031] flex-shrink-0" />
              These hours inform seeker matching and calendar sync
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#304031] flex-shrink-0" />
              You can adjust them later from your Schedule page
            </li>
          </ul>
        </div>

        {/* Navigation */}
        <div className="flex gap-4 items-center">
          <button
            onClick={() => navigate('/onboarding')}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full border border-[#304031] px-6 py-3 text-base font-semibold text-[#304031] transition hover:bg-[#304031]/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            ← Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            className={`flex items-center gap-2 rounded-full px-8 py-3 text-base font-semibold transition ${
              canSubmit && !saving
                ? 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            {saving && (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#fef6d6] border-t-transparent"></div>
            )}
            {saving ? 'Saving...' : 'Save & Continue'} →
          </button>
        </div>
      </div>
    </div>
  );
}
